<template>
    <section id="rsvp">
        <h2>RSVP</h2>
        <h4>Please RSVP by 17 November 2025</h4>
        <p class="mb-4">
            We're excited to celebrate our special day with you! Please let us know if you'll be joining us
            by completing the RSVP form below. If you have any dietary restrictions or special requirements,
            please let us know below.
        </p>
        <v-form>
            <div v-if="familyNotFound" class="error-message mt-4">
                <v-alert type="error" variant="tonal">
                    Sorry, your invitation could not be loaded. Please try again or send me a message so I can help you.
                </v-alert>
            </div>
            <div v-else class="guests-container mt-4">
                <div v-if="guests && guests.length" class="guests-list">
                    <div
                        v-for="(guest) in guests"
                        :key="guest.id"
                        class="guest-row"
                    >
                        <div class="guest-info">
                            <div class="guest-name">{{ guest.first_name }} {{ guest.last_name }}</div>
                            <div class="guest-attending">
                                <div class="switch-container">
                                    <v-switch
                                        v-model="guest.attending"
                                        :color="'var(--primary-green)'"
                                        hide-details
                                        @change="onAttendanceChange(guest)"
                                        :disabled="family?.rsvp_submitted && !isEditing"
                                        density="compact"
                                    >
                                        <template v-slot:prepend>
                                            <p class="switch-label">Not Attending</p>
                                        </template>
                                        <template v-slot:append>
                                            <p class="switch-label">Attending</p>
                                        </template>
                                    </v-switch>
                                </div>
                            </div>
                        </div>
                        <div v-if="guest.attending" class="dietary-section">
                            <v-textarea
                                v-model="guest.dietary"
                                variant="outlined"
                                hide-details
                                auto-grow
                                rows="1"
                                row-height="20"
                                :placeholder="isMobile ? 'Any dietary restrictions?' : 'Any dietary restrictions or preferences?'"
                                :disabled="family?.rsvp_submitted && !isEditing"
                            />
                        </div>
                    </div>
                </div>
                <v-skeleton-loader v-else type="text, text"></v-skeleton-loader>
            </div>
            <div v-if="family?.rsvp_submitted && !isEditing" class="rsvp-submitted-message mt-4">
                <v-alert variant="tonal" density="compact" color="var(--primary-green)">
                    Your RSVP has been successfully submitted.<br v-if="!isMobile" /> Would you like to edit your RSVP or dietary requirements?
                </v-alert>
            </div>
            <v-btn v-if="!familyNotFound && (!family?.rsvp_submitted || isEditing)" variant="outlined" class="mt-4" size="large" @click="openConfirmationDialog">
                {{ isEditing ? 'Submit RSVP' : 'Submit RSVP' }}
            </v-btn>
            <v-btn v-if="!familyNotFound && family?.rsvp_submitted && !isEditing" variant="outlined" size="large" @click="startEditing">
                Edit RSVP
            </v-btn>
        </v-form>
	</section>

    <!-- RSVP Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog" width="600px">
        <v-card class="confirmation-dialog">
            <img src="../assets/images/header_thin.png" alt="Header Image" class="dialog-header-image" />
                <v-card-text>
                    <div
                        v-if="attendingGuests.length > 0"
                        :style="attendingGuests.length > 0 && notAttendingGuests.length > 0 ? 'margin-bottom: 1.5rem;' : ''"
                    >
                        <h4 class="summary-title">{{ isMobile ? "Guests attending:" : "The following guests will be attending:"}}</h4>
                        <ul class="guest-list">
                            <li v-for="guest in attendingGuests" :key="guest.id" class="guest-item">
                                <span class="guest-name">{{ guest.first_name }} {{ guest.last_name }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-if="notAttendingGuests.length > 0">
                        <h4 class="summary-title">{{ isMobile ? "Guests not attending:" : "The following guests will not be attending:"}}</h4>
                        <ul class="guest-list">
                            <li v-for="guest in notAttendingGuests" :key="guest.id" class="guest-item">
                                <span class="guest-name">{{ guest.first_name }} {{ guest.last_name }}</span>
                            </li>
                        </ul>
                    </div>
                </v-card-text>
                <v-card-actions class="justify-center">
                    <v-btn @click="closeConfirmationDialog" variant="outlined" class="dialog-action-buttons">Cancel</v-btn>
                        <v-btn @click="submitRSVP" variant="outlined" class="dialog-action-buttons" :loading="submitting">
                            Send
                        <span class="send-icon">→</span>
                    </v-btn>
                </v-card-actions>
            <img src="../assets/images/header_thin.png" alt="Header Image" class="dialog-header-image bottom" />
        </v-card>
    </v-dialog>
</template>

<script>
import { supabase } from '@/lib/supabaseClient'
import { useIsMobile } from '@/lib/useIsMobile'

export default {
    name: "RsvpSection",

    props: {
        family: {
            type: Object,
            default: null
        },
        guests: {
            type: Array,
            default: () => []
        },
        familyNotFound: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
			confirmationDialog: false,
            submitting: false,
            isEditing: false,
        }
    },

    setup() {
        const { isMobile } = useIsMobile()
        return { isMobile }
    },

    computed: {
        attendingGuests() {
            return this.guests.filter(guest => guest.attending === true)
        },
        notAttendingGuests() {
            return this.guests.filter(guest => guest.attending === false || guest.attending === null || guest.attending === undefined)
        }
    },

	methods: {
		openConfirmationDialog() {
			this.confirmationDialog = true
		},

		closeConfirmationDialog() {
			this.confirmationDialog = false
		},

		onAttendanceChange(guest) {
			// Clear dietary requirements when unchecking attendance
			if (!guest.attending) {
				guest.dietary = ''
			}
		},

		scrollToSection(refName) {
			const el = this.$refs[refName]
			if (el) {
				el.scrollIntoView({ behavior: 'smooth' })
			}
		},

		async submitRSVP() {
			this.submitting = true
			try {
				// Update all guests in the database
				for (const guest of this.guests) {
					const { error } = await supabase
						.from('guests')
						.update({
							attending: guest.attending || false,
							dietary: guest.dietary || null,
                            responded_at: new Date().toISOString()
						})
						.eq('id', guest.id)

					if (error) {
						console.error('Error updating guest:', error)
						throw error
					}
				}

				// Update family's RSVP submitted status
				const { error: familyError } = await supabase
					.from('families')
					.update({
						rsvp_submitted: true
					})
					.eq('id', this.family.id)

				if (familyError) {
					console.error('Error updating family RSVP status:', familyError)
					throw familyError
				}

				// Emit event to refresh data in parent component
				this.$emit('refresh-data', this.family.id)

				// Close dialog and show success message
				this.closeConfirmationDialog()
				this.isEditing = false
				// You could add a success notification here
				console.log('RSVP submitted successfully')
			} catch (error) {
				console.error('Failed to submit RSVP:', error)
				// You could add an error notification here
			} finally {
				this.submitting = false
			}
		},

		startEditing() {
			this.isEditing = true
		},
	}
}
</script>

<style scoped>
.guests-container {
    border: 1px solid darkgrey;
    border-radius: 0.5rem;
    text-align: left;
}

.guests-list {
    padding: 0 1rem 0 1rem;
}

.guest-header {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e0e0e0;
    font-weight: 600;
    color: #666;
}

 .guest-row {
    border-bottom: 1px solid #e0e0e0;
    padding: 0;
 }

.guest-row:last-child {
    border-bottom: none;
}

 .guest-info {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1rem;
    align-items: center;
 }

.guest-name {
    font-weight: 500;
}

 .guest-attending {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
 }

 .switch-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
 }


 .switch-label {
    font-size: 0.8rem;
 }

 .dietary-section {
    margin-top: 0.5rem;
    padding-bottom: 1rem;
 }

.dietary-label {
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: #666;
}

:deep(.v-selection-control--disabled.v-selection-control--dirty) .v-switch__track {
    background-color: var(--primary-green) !important;
}

:deep(.v-selection-control--disabled.v-selection-control--dirty) .v-selection-control__input .v-switch__thumb {
    background-color: var(--primary-green) !important;
}

/* Mobile responsive styles */
@media (max-width: 767.98px) {
    .guest-header {
        grid-template-columns: 1fr auto;
        gap: 0.5rem;
        font-size: 0.9rem;
    }

    .guest-info {
        grid-template-columns: 1fr auto;
        gap: 0.5rem;
    }

    .guest-name {
        font-size: 0.9rem;
    }

    .guests-list {
        padding: 0.75rem;
    }

        .guest-row {
            padding: 0;
    }

    .dietary-section {
        margin-top: 0;
        padding-bottom: 1rem;
    }
}

 .confirmation-dialog {
     width: 600px;
     padding: 0;
     text-align: center;
     overflow: hidden;
     font-family: "Playfair", serif;
 }

 .dialog-header-image {
     display: block;
     width: 100%;
     object-fit: cover;
     object-position: center;
     margin: 0;
 }

 .dialog-header-image.bottom {
     transform: rotate(180deg);
 }

 .confirmation-dialog :deep(.v-card-title) {
     white-space: normal;
     word-wrap: break-word;
     line-height: 1.4;
     padding: 1rem 1rem 0.5rem 1rem;
     font-family: "Playfair", serif;
 }

 .confirmation-dialog :deep(.v-card-text) {
     padding: 0 1rem 1rem 1rem;
     font-family: "Playfair", serif;
 }

 .confirmation-dialog :deep(.v-card-actions) {
     padding: 0 1rem 1rem 1rem;
     font-family: "Playfair", serif;
 }

@media (max-width: 767.98px) {
    .confirmation-dialog {
        width: 100%;
    }
}

.dialog-action-buttons {
     width: 120px;
     margin: 0 0.5rem;
 }

 .summary-title {
     font-weight: 600;
     margin-bottom: 0.5rem;
     font-size: 1rem;
 }

 .summary-title.attending {
     color: var(--primary-green);
 }

 .summary-title.not-attending {
     color: #d32f2f;
 }



 .guest-list {
     padding: 0;
     margin: 0 auto;
     text-align: left;
     display: inline-block;
     min-width: fit-content;
 }

 .guest-item {
     padding: 0.25rem 0;
     list-style-type: circle;
     margin-left: 1rem;
 }

 .guest-name {
     font-weight: 500;
 }

 .dietary-note {
     color: #666;
     font-style: italic;
     font-size: 0.9rem;
 }

 .send-icon {
     margin-left: 0.5rem;
     font-size: 1.2rem;
     font-weight: bold;
 }

 .rsvp-submitted-message {
     margin-bottom: 1rem;
 }
</style>
